import { chatAbortControllersAtom, chatStateAtom } from '@/atoms/chatAtoms';
import type { ErrorItemEntry, TimelineEntry } from '@heidi/evidence/contracts';
import { IS_PROGRESSIVE_STREAM_TIMEOUT_ENABLED } from '@/app-shell/flags/evidence-flags';
import { finishTracking, TrackingKey } from '@heidi/runtime/performance';
import { TIMELINE_STREAM_ERROR } from '@heidi/evidence/data';
import { logger } from '@/utils/logger/logger';
import { abortStreamWithReason, STREAM_ABORT_REASON } from '@/utils/streamAbortReason';
import { getDefaultStore, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

/** Hard abort: no chunks at all for 5 minutes (connection dead). */
const STREAM_TIMEOUT = 300_000;
/** Soft timeout: no visible content for 30s — show error + retry (EVI-1493). */
const VISIBLE_CONTENT_TIMEOUT = 30_000;
/** Warning: no visible content for 15s — show "taking longer than expected" hint. */
const VISIBLE_CONTENT_WARNING = 15_000;

const CHECK_INTERVAL = 5_000;

/**
 * A tool card is finished. Any other status (`searching`, `generating`,
 * `fetching`, …) means the tool is still running and the next chunk is the
 * result, which can take longer than the 30s visible-content window.
 *
 * `needs_template` is not in this set. Form fill has stopped its own work and
 * is showing a picker; treating that as finished would replace the picker
 * with the timeout error while the stream is still open.
 */
const TERMINAL_TOOL_STATUSES: ReadonlySet<string> = new Set([
  'completed',
  'complete',
  'error',
  'failed',
]);

/**
 * True when this response already shows a tool card that has not finished.
 * Thinking is excluded: its deltas reset `lastVisibleContentAt`, and a silent
 * thinking stall should still soft-abort (EVI-2477).
 */
const hasInFlightTool = (timeline: TimelineEntry[], basePosition: number): boolean =>
  timeline.some((entry) => {
    if (entry.position < basePosition || entry.type === 'thinking_item') return false;
    if (!('status' in entry) || typeof entry.status !== 'string') return false;
    return !TERMINAL_TOOL_STATUSES.has(entry.status);
  });

/**
 * Why the watcher aborted a stream. Surfaced as `timeout_reason` on the
 * terminal perf-tracking event so the two client-side timeouts (5-min dead
 * connection vs 30s no-visible-content) can be told apart in Datadog RUM.
 */
type StreamTimeoutReason = 'hard_no_activity' | 'soft_no_visible_content';

/**
 * Tracks cumulative time the page has spent hidden (backgrounded on mobile).
 * This offset is added to `lastChunkAt` so that background time doesn't
 * count toward the stream timeout.
 */
export const usePageHiddenOffset = () => {
  const offsetRef = useRef(0);
  const hiddenSinceRef = useRef<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now();
      } else if (hiddenSinceRef.current !== null) {
        offsetRef.current += Date.now() - hiddenSinceRef.current;
        hiddenSinceRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return { offsetRef, hiddenSinceRef };
};

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/**
 * Global timeout watcher that monitors ALL active streams across all chats.
 * This ensures streams are monitored even when switching between chats.
 *
 * Progressive timeout strategy (EVI-1493 / EVI-2477 / EVI-2622):
 * 1. 15s with no visible content → set `streamSlow` flag (UI shows hint)
 * 2. 30s with no visible content → abort stream + show error with retry button
 * 3. 5 min with no chunks at all → hard abort (connection dead)
 *
 * "Visible content" = text, search, thinking, reasoning, tool cards, etc.
 * Keepalives do NOT count — they only bump `lastChunkAt`.
 *
 * A tool card that is still non-terminal pauses steps 1–2. evidence_search and
 * Word export emit one status chunk, then block until the tool returns — that
 * wait is progress, and it can outlast 30s. Step 3 still fires if chunks and
 * keepalives stop.
 *
 * OPTIMIZATION: This hook reads chatState from store.get() in the interval
 * instead of subscribing via useAtom, preventing unnecessary re-renders
 * when chat state changes during streaming.
 */
export const useGlobalStreamTimeoutWatcher = () => {
  const setChatState = useSetAtom(chatStateAtom);
  const storeRef = useRef(getDefaultStore());
  const store = storeRef.current;
  const { offsetRef, hiddenSinceRef } = usePageHiddenOffset();
  const warnedKeysRef = useRef(new Set<string>());

  const handleStreamTimeout = useCallback(
    (stateKey: string, reason: StreamTimeoutReason) => {
      const abortControllerAtom = chatAbortControllersAtom(stateKey);
      const abortController = store.get(abortControllerAtom);
      if (abortController) {
        abortStreamWithReason(abortController, STREAM_ABORT_REASON.GLOBAL_TIMEOUT);
        store.set(abortControllerAtom, null);
      }

      warnedKeysRef.current.delete(stateKey);

      // Read the chat session snapshot once so both correlated fields derive
      // from the same state and we avoid a repeated store lookup.
      const sessionSnapshot = store.get(chatStateAtom)?.[stateKey];

      // Correlate this terminal event with the chat the same way
      // useChatStream's success/error terminals do: read the resolved
      // client-generated chat id from state. The submit-time chat_id already on
      // the perf entry is often still null for new chats, so pass the resolved
      // one to override it. routed_mode is intentionally not set — it lives in a
      // useChatStream-local ref, not chat state, so it isn't reachable here and
      // is left off rather than guessed.
      const resolvedChatId = sessionSnapshot?.metadata?.clientChatId ?? null;

      // message_id comes from the message_info chunk (backendIndex -1, before
      // content). It's present on a soft timeout (content stalled after the
      // stream opened) and null on a hard timeout where no chunk ever arrived.
      const resolvedMessageId = sessionSnapshot?.streamingState?.messageId ?? null;

      // Nulling streamingState below makes the abort-triggered error path in
      // useChatStream treat this run as stale and skip its own finishTracking,
      // so without this the perf-tracking entry leaks — never reported to RUM,
      // or only captured as "abandoned" on page exit. Report the terminal
      // outcome here so timed-out ("stuck") sends still land in Datadog RUM.
      // Both message-send keys are finished (the inactive one no-ops), matching
      // the paired finishTracking in useChatStream's success/error handlers.
      const timeoutMetadata = {
        result: 'error',
        error_type: 'client_timeout',
        timeout_reason: reason,
        chat_id: resolvedChatId,
        message_id: resolvedMessageId,
      };
      finishTracking({ key: TrackingKey.EVIDENCE_MESSAGE_SEND, metadata: timeoutMetadata });
      finishTracking({
        key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
        metadata: timeoutMetadata,
      });

      setChatState((prev) => {
        const session = prev[stateKey];
        if (!session) return prev;

        const streamingState = session.streamingState;
        if (!streamingState) return prev;

        const basePosition = streamingState.basePosition;
        const timelineBeforeStream = session.timeline.slice(0, basePosition);

        const errorEntry: ErrorItemEntry = {
          type: 'error_item',
          id: generateId(),
          content: TIMELINE_STREAM_ERROR.QUERY_TIMEOUT,
          position: basePosition,
        };

        return {
          ...prev,
          [stateKey]: {
            ...session,
            timeline: [...timelineBeforeStream, errorEntry] as typeof session.timeline,
            streamingState: null,
          },
        };
      });

    },
    [store, setChatState]
  );

  useEffect(() => {
    const checkForTimeouts = () => {
      if (document.hidden) return;

      const now = Date.now();
      const currentChatState = store.get(chatStateAtom);

      const currentHiddenDuration = hiddenSinceRef.current
        ? now - hiddenSinceRef.current
        : 0;
      const totalHiddenOffset = offsetRef.current + currentHiddenDuration;

      for (const [stateKey, session] of Object.entries(currentChatState)) {
        const streamingState = session?.streamingState;
        if (!streamingState) {
          warnedKeysRef.current.delete(stateKey);
          continue;
        }

        const timeSinceLastChunk = now - streamingState.lastChunkAt - totalHiddenOffset;

        // Hard abort: no chunks at all for 5 minutes (connection dead)
        if (timeSinceLastChunk > STREAM_TIMEOUT) {
          logger.error('Stream timeout detected - no activity', {
            stateKey,
            timeSinceLastChunk,
            timeSinceStart: now - streamingState.streamStartedAt,
            hiddenOffset: totalHiddenOffset,
            activeIndices: Array.from(streamingState.activeIndices.entries()),
          });
          handleStreamTimeout(stateKey, 'hard_no_activity');
          continue;
        }

        // Progressive soft timeout (15s slow hint, 30s abort) is gated behind
        // release-evidence-progressive-stream-timeout. It aborts on no visible
        // content, which can cut valid slow-but-working streams, so it ships
        // flag-off and rolls out gradually (staging → prod %). The 5-minute hard
        // abort above stays always-on as the final backstop.
        if (!IS_PROGRESSIVE_STREAM_TIMEOUT_ENABLED()) continue;

        // EVI-2622: pause the visible-content clock while a current-response
        // tool card is still running. The status chunk already counted as
        // progress; the result can take longer than 30s.
        if (hasInFlightTool(session.timeline, streamingState.basePosition)) continue;

        const timeSinceVisibleContent =
          now - streamingState.lastVisibleContentAt - totalHiddenOffset;

        // A thinking/text chunk clears `streamSlow` in processChatChunk. Drop
        // the one-shot warning key too so a later stall can re-arm the hint
        // instead of jumping straight to the 30s abort (EVI-2477).
        if (timeSinceVisibleContent <= VISIBLE_CONTENT_WARNING) {
          warnedKeysRef.current.delete(stateKey);
        }

        // Soft timeout: no visible content for 30s — abort + show retry
        if (timeSinceVisibleContent > VISIBLE_CONTENT_TIMEOUT) {
          logger.warn('Stream soft timeout - no visible content for 30s', {
            stateKey,
            timeSinceVisibleContent,
            timeSinceLastChunk,
            lastVisibleContentAt: streamingState.lastVisibleContentAt,
            lastChunkAt: streamingState.lastChunkAt,
          });
          handleStreamTimeout(stateKey, 'soft_no_visible_content');
          continue;
        }

        // Warning: no visible content for 15s — flag stream as slow
        if (
          timeSinceVisibleContent > VISIBLE_CONTENT_WARNING &&
          !warnedKeysRef.current.has(stateKey)
        ) {
          warnedKeysRef.current.add(stateKey);
          setChatState((prev) => {
            const s = prev[stateKey];
            if (!s?.streamingState) return prev;
            if (s.streamingState.streamSlow) return prev;
            return {
              ...prev,
              [stateKey]: {
                ...s,
                streamingState: { ...s.streamingState, streamSlow: true },
              },
            };
          });
        }
      }
    };

    const intervalId = setInterval(checkForTimeouts, CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [store, handleStreamTimeout, setChatState, offsetRef, hiddenSinceRef]);
};

export { STREAM_TIMEOUT, VISIBLE_CONTENT_TIMEOUT, VISIBLE_CONTENT_WARNING, CHECK_INTERVAL };
